/*
 * Swym wishlist glue: custom <swymcs-wishlist-button> element + the helper API
 * that predictive-search.js and the wishlist page call through
 * `window.swymcsWishlistFunctions`.
 *
 * This used to live inline in snippets/swym-custom.liquid as
 * `<script defer>...</script>`. `defer` is ignored on inline scripts, so 18KB
 * of JS was parsed and executed synchronously in the middle of <body> - right
 * after the header group and BEFORE <main> - stalling the HTML parser before
 * any page content could paint. It also shipped inside every HTML response
 * instead of being cached once.
 *
 * Two behaviour-preserving performance fixes were made while moving it:
 *   1. _observeWishlistGrid used to create one MutationObserver per button, on
 *      document.body with subtree: true. A collection page with 96 cards meant
 *      96 whole-document observers all running the same querySelector on every
 *      mutation. It is now a single shared observer.
 *   2. The ~40 debug console.log calls - several of them in click handlers and
 *      in per-button init - are behind `window.SWYM_DEBUG`. Set it to true in
 *      the console to get the old output back.
 *
 * Top-level `const`s are intentional: they were script-scoped globals before
 * and other code may still reach them, so the scoping is unchanged.
 */

const swymDebug = (...args) => {
  if (window.SWYM_DEBUG) console.log(...args);
};

const swymWarn = (...args) => {
  if (window.SWYM_DEBUG) console.warn(...args);
};

const addToWishlist = (swat, product) => {
  swymDebug('[Swym] Adding to wishlist:', product, 'List ID:', window.swymSelectedListId);
  let onSuccess = async function (addedListItem) {
    swymDebug('[Swym] Added successfully:', addedListItem);
    updateButtonState();
  };

  let onError = function (error) {
    swymDebug('[Swym] Error adding:', error);
    if (swat && swat.ui) {
      swat.ui.showErrorNotification({ message: 'Error Adding Product to Wishlist' });
    }
  };

  let lid = window.swymSelectedListId;
  if (!lid) {
    swymWarn('[Swym] No list ID set, cannot add to wishlist');
    return;
  }
  if (!swat || !swat.addToList) {
    swymWarn('[Swym] swat.addToList not available');
    return;
  }
  swymDebug('[Swym] Calling swat.addToList with:', { lid, product });
  swat.addToList(lid, product, onSuccess, onError);
};

const removeFromWishlist = (swat, product) => {
  swymDebug('[Swym] Removing from wishlist:', product, 'List ID:', window.swymSelectedListId);
  let onSuccess = async function (deletedProduct) {
    swymDebug('[Swym] Removed successfully:', deletedProduct);
    updateButtonState();
  };

  let onError = function (error) {
    swymDebug('[Swym] Error removing:', error);
    if (swat && swat.ui) {
      swat.ui.showErrorNotification({ message: 'Error removing Product from Wishlist' });
    }
  };

  let lid = window.swymSelectedListId;
  if (!lid) {
    swymWarn('[Swym] No list ID set, cannot remove from wishlist');
    return;
  }
  if (!swat || !swat.deleteFromList) {
    swymWarn('[Swym] swat.deleteFromList not available');
    return;
  }
  swymDebug('[Swym] Calling swat.deleteFromList with:', { lid, product });
  swat.deleteFromList(lid, product, onSuccess, onError);
};

const fetchList = async (swat) => {
  return new Promise((resolve, reject) => {
    const onSuccess = (lists) => {
      window.swymWishLists = lists;
      const selectedList = lists?.find((list) => list.lname?.toLowerCase() === 'my wishlist') || lists?.[0];
      window.swymSelectedListId = selectedList?.lid;
      resolve(lists);
      updateButtonState();
    };

    const onError = (error) => {
      reject(error);
    };

    if (!window.swymWishLists) {
      swat.fetchLists({
        callbackFn: onSuccess,
        errorFn: onError,
      });
    } else {
      resolve(window.swymWishLists);
    }
  });
};

const createList = (swat) => {
  let listConfig = { lname: 'My Wishlist' };

  let onSuccess = function ({ lid }) {
    window.swymSelectedListId = lid;
  };

  let onError = function (error) {
    swymDebug('Error while creating a List', error);
  };

  swat.createList(listConfig, onSuccess, onError);
};

const refreshList = async (swat) => {
  window.swymWishLists = null;
  await fetchList(swat);
};

const init = (swat) => {
  swymDebug('[Swym] Init called, fetching list...');
  fetchList(swat).then(() => {
    swymDebug('[Swym] List fetched. swymSelectedListId:', window.swymSelectedListId);
    if (!window.swymSelectedListId) {
      swymDebug('[Swym] No list ID, creating new list...');
      createList(swat);
    }
  });
  bindSwymEvents(swat);
};

const updateButtonState = () => {
  const swymWishlistButtons = document.querySelectorAll('swymcs-wishlist-button');
  if (swymWishlistButtons) {
    swymWishlistButtons.forEach((swymWishlistButton) => {
      const elements = swymWishlistButton.elements;
      if (!elements || !elements.wishlistButton) return;
      const { epi, empi } = elements.wishlistButton.dataset;
      let isWishlisted = false;
      if (window.swymWishLists) {
        window.swymWishLists.forEach((list) => {
          list.listcontents.forEach((item) => {
            if (item.empi == empi && item.epi == epi) {
              isWishlisted = true;
            }
          });
        });
        updateButtonWishlistState(elements, isWishlisted);
      }
    });
  }
};

const updateButtonWishlistState = (elements, isWishlisted) => {
  let settings = JSON.parse(elements.wishlistButton.dataset.settings);
  elements.filledState.style.display = isWishlisted ? 'flex' : 'none';
  elements.unfilledState.style.display = isWishlisted ? 'none' : 'flex';
  if (isWishlisted) {
    elements.wishlistButton.classList.add('swym-added-custom');
  } else {
    elements.wishlistButton.classList.remove('swym-added-custom');
    elements.wishlistButton.classList.remove('disabled');
    elements.wishlistButton.removeAttribute('disabled');
  }
};

// Debounce object to prevent duplicate notifications
const lastAction = {};

const bindSwymEvents = (swat) => {
  swat.evtLayer.addEventListener(swat.JSEvents.removedFromWishlist, (event) => {
    const { iu: image, dt: title, empi } = event.detail.d;
    if (lastAction[empi] === 'remove') return; // skip duplicate
    lastAction[empi] = 'remove';

    showCustomNotification(swat, image, title, 'remove', 1);

    // Refresh list silently
    window.swymWishLists = null;
    fetchList(swat).then(() => updateButtonState());

    setTimeout(() => {
      delete lastAction[empi];
    }, 1000); // reset after 1s
  });

  swat.evtLayer.addEventListener(swat.JSEvents.addedToWishlist, (event) => {
    const { iu: image, dt: title, empi } = event.detail.d;
    if (lastAction[empi] === 'add') return; // skip duplicate
    lastAction[empi] = 'add';

    showCustomNotification(swat, image, title, 'add', 1);

    // Refresh list silently
    window.swymWishLists = null;
    fetchList(swat).then(() => updateButtonState());

    setTimeout(() => {
      delete lastAction[empi];
    }, 1000); // reset after 1s
  });

  swat.evtLayer.addEventListener(swat.JSEvents.variantChanged, (data) => {
    const currentVariant = data.detail.d.variant.id;

    // Select all buttons with ID = 'swymcs-wishlist-button'
    const wishlistButtons = document.querySelectorAll('button#swymcs-wishlist-button');

    wishlistButtons.forEach((button) => {
      button.setAttribute('data-epi', currentVariant);
    });

    setTimeout(updateButtonState, 200);
  });
};

const showCustomNotification = (swat, image, title, action, listCount) => {
  let successMessage = `
      <style>
        .swym-notification-success-inner .swym-image{ display: none !important }
        #swym-custom-notification-content { display: flex; gap: 10px; align-items: center; cursor: pointer }
        #swym-custom-notification-content #swym-custom-image-container { min-height: 50px; min-width: 50px; max-height: 50px; max-width: 50px }
        #swym-custom-notification-content #swym-custom-image-container #swym-custom-image { width: 100%; height: 100%; object-fit: cover; border-radius: 50% }
        #swym-custom-notification-content #swym-custom-title-container { font-weight: 500; font-size: 14px }
      </style>
      <div id="swym-custom-notification-content" onclick="window.location.href='https://nude-project.com/pages/swym-wishlist'">
        <div id="swym-custom-image-container">
          <img id="swym-custom-image" src="${image}" alt="wishlisted product image"/>
        </div>
        <div id="swym-custom-title-container"><strong>${title}</strong> has been ${
    action === 'remove' ? 'removed from' : 'added to'
  } <strong>${listCount == 1 ? 'list!' : 'lists!'}</strong></div>
      </div>
    `;
  swat.ui.showSuccessNotification({ message: successMessage });
};

// Mock for missing function (if you don't use this, you can remove it)
const renderPopup = (product) => {
  swymDebug('Render popup for product', product);
};

// Optional if needed
const fetchProductsData = () => {};

/*
 * The wishlist heart button on grid-style product cards is normally
 * auto-injected by the Swym app-embed script, which scans the page for
 * `.card-wrapper.product-card-wrapper` once on load (its selector lives in
 * the Swym "Collections" app-embed settings, config/settings_data.json).
 * Any card added to the DOM afterwards - via JS, after the initial scan -
 * never gets that scan and stays without a button. That already happens for:
 *   - predictive-search.js's dropdown results
 *   - the related-products "Similar Items" rail (ProductRecommendations in
 *     global.js fetches and injects its cards)
 *   - the related-products "Recently Viewed" rail (tabbed-related-products.js)
 * so all three call this after inserting their markup, building the same
 * button Swym would have, wired into the existing [data-wishlist-trigger]
 * click handling above. Relying on Swym's own selector list to cover these
 * cards is fragile: a Theme Customizer save re-syncs settings_data.json from
 * the live app-embed settings and has silently dropped compound selectors
 * added there before.
 */
const renderCardWishlistButtons = (root) => {
  (root || document).querySelectorAll('.card-wrapper.product-card-wrapper').forEach((card) => {
    if (card.querySelector('.swym-wishlist-collections-v2-container')) return;

    const productId = card.dataset.productId;
    const productUrl = card.dataset.productUrl;
    if (!productId || !productUrl) return;

    let variantId = '';
    const productJson = card.querySelector('[data-card-product-json]');
    if (productJson) {
      try {
        const productData = JSON.parse(productJson.textContent);
        const variant = productData.variants?.find((v) => v.available) || productData.variants?.[0];
        variantId = variant?.id || '';
      } catch (error) {
        // Ignore malformed product JSON, button still renders without an epi
      }
    }

    const container = document.createElement('div');
    container.className = 'swym-wishlist-collections-v2-container';
    container.style.position = 'absolute';
    container.style.top = '0';
    container.style.zIndex = '2';
    container.innerHTML = `
      <button
        type="button"
        class="swym-wishlist-collections-v2"
        data-wishlist-trigger
        data-epi="${variantId}"
        data-empi="${productId}"
        data-du="${window.location.origin}${productUrl}"
        aria-label="Add to wishlist"
        aria-pressed="false"
      ></button>
    `;
    card.prepend(container);
  });

  window.swymcsWishlistFunctions?.updateButtonState();
};

// =================== Assign to Global Object ===================

const swymcsWishlistFunctions = {
  init,
  fetchList,
  createList,
  refreshList,
  updateButtonState,
  updateButtonWishlistState,
  addToWishlist,
  removeFromWishlist,
  addSwymEventListener: bindSwymEvents,
  fetchProductsData,
  renderPopup,
  showCustomNotification,
  renderCardWishlistButtons,
};

window.swymcsWishlistFunctions = swymcsWishlistFunctions;

if (!window.SwymCallbacks) {
  window.SwymCallbacks = [];
}
window.SwymCallbacks.push(swymcsWishlistFunctions.init);

// Global listener for all wishlist trigger clicks
document.addEventListener(
  'click',
  (e) => {
    const wishlistBtn = e.target.closest('[data-wishlist-trigger]');
    if (wishlistBtn) {
      swymDebug('[Swym-Global] Wishlist button clicked');
      e.preventDefault();
      e.stopPropagation();

      const product = {
        epi: wishlistBtn.dataset.epi,
        empi: wishlistBtn.dataset.empi,
        du: wishlistBtn.dataset.du,
      };

      swymDebug('[Swym-Global] Product data:', product);

      if (!window._swat) {
        swymWarn('[Swym-Global] Swym not loaded');
        return;
      }

      if (!window.swymSelectedListId) {
        swymWarn('[Swym-Global] No list ID, fetching...');
        swymcsWishlistFunctions.fetchList(window._swat).then(() => {
          handleWishlistClick(wishlistBtn, product);
        });
        return;
      }

      handleWishlistClick(wishlistBtn, product);
    }
  },
  true
);

function handleWishlistClick(btn, product) {
  if (btn.classList.contains('swym-added-custom')) {
    swymDebug('[Swym-Global] Removing from wishlist');
    swymcsWishlistFunctions.removeFromWishlist(window._swat, product);
  } else {
    swymDebug('[Swym-Global] Adding to wishlist');
    swymcsWishlistFunctions.addToWishlist(window._swat, product);
  }
}

/*
 * One shared body observer for the whole document instead of one per
 * <swymcs-wishlist-button>. The previous per-instance observers all watched
 * document.body with subtree: true and all ran the same
 * `querySelector('.swym-wishlist-grid')` on every single DOM mutation, so the
 * cost scaled with (number of cards) x (number of mutations).
 */
const swymWishlistGridWatcher = (() => {
  let started = false;
  let boundGrid = null;
  let deleteObserver = null;

  const setupDeleteButtonListeners = (grid) => {
    grid.querySelectorAll('.swym-delete-btn').forEach((btn) => {
      if (btn.dataset.swymDeleteBound) return;
      btn.dataset.swymDeleteBound = 'true';
      btn.addEventListener('click', () => {
        setTimeout(() => {
          // Counter refresh was already commented out upstream; the hook is
          // kept so the listener contract does not change.
        }, 100);
      });
    });
  };

  const bindGrid = (grid) => {
    if (boundGrid === grid) return;
    boundGrid = grid;
    if (deleteObserver) deleteObserver.disconnect();
    setupDeleteButtonListeners(grid);
    deleteObserver = new MutationObserver(() => setupDeleteButtonListeners(grid));
    deleteObserver.observe(grid, { childList: true, subtree: true });
  };

  return {
    start() {
      if (started) return;
      started = true;

      const existing = document.querySelector('.swym-wishlist-grid');
      if (existing) bindGrid(existing);

      const observer = new MutationObserver(() => {
        const grid = document.querySelector('.swym-wishlist-grid');
        if (grid) bindGrid(grid);
      });
      observer.observe(document.body, { childList: true, subtree: true });
    },
  };
})();

// =================== Custom Element ===================

if (!customElements.get('swymcs-wishlist-button')) {
  class SwymWishlistButton extends HTMLElement {
    constructor() {
      super();

      // Ensure SwymCallbacks exists
      if (!window.SwymCallbacks) {
        window.SwymCallbacks = [];
      }

      // Push initialize callback
      window.SwymCallbacks.push(this._initializeSwymButton.bind(this));

      // Bind the wishlist click handler once
      this._handleWishlistClickBound = this._handleWishlistClick.bind(this);
      this.addEventListener('click', this._handleWishlistClickBound);

      // Shared, idempotent - not one observer per button.
      swymWishlistGridWatcher.start();
    }

    /**
     * Handle wishlist button clicks
     */
    _handleWishlistClick(e) {
      swymDebug('[Swym] Click event:', e.type, 'Target:', e.target);
      const button = e.target.closest('button');
      if (!button) {
        swymWarn('[Swym] No button found from click target');
        return;
      }

      // If elements not cached, try to cache them now
      if (!this.elements?.wishlistButton) {
        swymWarn('[Swym] Elements not cached, caching now...');
        this.elements = {
          wishlistButton: this.querySelector('button'),
          unfilledState: this.querySelector('#swymUnfilled'),
          filledState: this.querySelector('#swymFilled'),
        };
      }

      if (!this.elements?.wishlistButton) {
        swymWarn('[Swym] Still no wishlistButton found');
        return;
      }

      if (!window._swat) {
        swymWarn('[Swym] Swym library not loaded yet');
        return;
      }

      if (!window.swymSelectedListId) {
        swymWarn('[Swym] List ID not set, initializing...');
        swymcsWishlistFunctions.fetchList(window._swat).then(() => {
          swymDebug('[Swym] List fetched, retrying click');
          this._handleWishlistClick(e);
        });
        return;
      }

      const { epi, empi, du } = this.elements.wishlistButton.dataset;
      const product = { epi, empi, du };
      swymDebug('[Swym] Button data:', product);

      if (window._swat.isCollectionsEnabled()) {
        swymcsWishlistFunctions.renderPopup(product);
      } else {
        if (this.elements.wishlistButton.classList.contains('swym-added-custom')) {
          swymcsWishlistFunctions.removeFromWishlist(window._swat, product);
        } else {
          swymcsWishlistFunctions.addToWishlist(window._swat, product);
        }
      }
    }

    /**
     * Initialize wishlist button and counter
     */
    async _initializeSwymButton(swat) {
      this.elements = {
        wishlistButton: this.querySelector('button'),
        unfilledState: this.querySelector('#swymUnfilled'),
        filledState: this.querySelector('#swymFilled'),
      };

      swymDebug('[Swym-Init] Button initialized:', this.elements.wishlistButton);
    }

    /**
     * Helper: fetch wishlist data
     */
    getListsData(swat) {
      return new Promise((resolve, reject) => {
        swat.fetchLists({
          callbackFn: (data) => resolve(data),
          onError: (error) => reject(error),
        });
      });
    }
  }

  customElements.define('swymcs-wishlist-button', SwymWishlistButton);
}
